import { useQuery } from '@tanstack/react-query'
import { CheckCircle, Clock, Mail, AlertTriangle, XCircle, Eye } from 'lucide-react'
import { listReportDeliveries, type ReportDelivery } from '../../lib/services/facilityContacts'

const STATUS_CONFIG: Record<
  ReportDelivery['status'],
  { label: string; icon: React.ElementType; className: string }
> = {
  queued:    { label: 'Queued',    icon: Clock,         className: 'text-slate-500 bg-slate-100' },
  sent:      { label: 'Sent',      icon: Mail,          className: 'text-blue-600 bg-blue-50' },
  delivered: { label: 'Delivered', icon: CheckCircle,   className: 'text-green-600 bg-green-50' },
  opened:    { label: 'Opened',    icon: Eye,           className: 'text-teal-600 bg-teal-50' },
  bounced:   { label: 'Bounced',   icon: AlertTriangle, className: 'text-amber-600 bg-amber-50' },
  failed:    { label: 'Failed',    icon: XCircle,       className: 'text-red-600 bg-red-50' },
}

function StatusBadge({ status, bounceReason }: { status: ReportDelivery['status']; bounceReason: string | null }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.sent
  const Icon = cfg.icon
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${cfg.className}`}
      title={status === 'bounced' && bounceReason ? bounceReason : undefined}
    >
      <Icon size={12} />
      {cfg.label}
    </span>
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

interface DeliveryLogProps {
  reportId: string
}

export function DeliveryLog({ reportId }: DeliveryLogProps) {
  const { data: deliveries = [], isLoading } = useQuery({
    queryKey: ['report-deliveries', reportId],
    queryFn: () => listReportDeliveries(reportId),
    enabled: !!reportId,
    refetchInterval: 30_000,
  })

  if (isLoading) {
    return <div className="py-4 text-sm text-slate-500">Loading delivery log…</div>
  }

  if (deliveries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
        No deliveries yet. Send the report to log a delivery.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Sent</th>
            <th className="px-4 py-2.5">Recipient</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Delivered</th>
            <th className="px-4 py-2.5 font-mono">PDF Hash</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {deliveries.map((d) => (
            <tr key={d.delivery_id} className="hover:bg-slate-50">
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{formatDate(d.sent_at)}</td>
              <td className="px-4 py-2.5">
                <div className="font-semibold text-slate-800">{d.contact_name}</div>
                <div className="text-xs text-slate-500">{d.contact_email}</div>
              </td>
              <td className="px-4 py-2.5">
                <StatusBadge status={d.status} bounceReason={d.bounce_reason} />
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                {formatDate(d.delivered_at ?? d.opened_at)}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-slate-400" title={d.pdf_sha256}>
                {d.pdf_sha256.slice(0, 12)}…
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
